import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchChatBootstrap } from '@/services/chat.js';
import { createConversationMessageSnapshot, findNewIncomingMessages, getChatNoticeMode, sumUnreadCounts } from '@/pages/Chat/chatNotifications.js';
import { useSetChatUnreadCount } from './useChatUnreadCount.js';

const POLL_INTERVAL_MS = 6000;
const NOTICE_DURATION_MS = 6000;

export default function ChatNotificationsMonitor() {
  const location = useLocation();
  const navigate = useNavigate();
  const setTotalUnread = useSetChatUnreadCount();
  const previousSnapshotRef = useRef(null);
  const [notices, setNotices] = useState([]);

  useEffect(() => {
    let mounted = true;

    const poll = async () => {
      try {
        const bootstrap = await fetchChatBootstrap();
        if (!mounted) return;
        if (bootstrap?.chatUnavailable) {
          setTotalUnread(0);
          return;
        }

        const conversations = Array.isArray(bootstrap?.conversations) ? bootstrap.conversations : [];
        setTotalUnread(sumUnreadCounts(conversations));
        const nextSnapshot = createConversationMessageSnapshot(conversations);
        const currentUserId = bootstrap?.me?.id;
        const newMessages = findNewIncomingMessages(previousSnapshotRef.current, conversations, currentUserId);
        previousSnapshotRef.current = nextSnapshot;

        if (!newMessages.length) return;

        const hidden = document.visibilityState === 'hidden';
        const chatRouteActive = location.pathname === '/chat' || location.pathname.startsWith('/chat/');
        const permission = typeof window !== 'undefined' && 'Notification' in window
          ? Notification.permission
          : 'denied';
        const mode = getChatNoticeMode({ hidden, chatRouteActive, permission });

        if (mode === 'system') {
          newMessages.forEach((message) => {
            const notification = new Notification(`Mensaje de ${message.senderName}`, {
              body: message.body,
              icon: '/favicon.ico',
              tag: `chat-${message.conversationId}-${message.messageId}`,
            });
            notification.onclick = () => {
              window.focus();
              navigate('/chat');
              notification.close();
            };
          });
        } else if (mode === 'in-app') {
          const timestamp = Date.now();
          setNotices(current => [
            ...current,
            ...newMessages.map((message, index) => ({ ...message, noticeId: `${message.conversationId}-${message.messageId}-${timestamp}-${index}` })),
          ].slice(-3));
        }
      } catch {
        // Chat may be unavailable in some tenants/environments; keep the shell usable.
      }
    };

    void poll();
    const intervalId = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, [location.pathname, navigate, setTotalUnread]);

  useEffect(() => {
    if (!notices.length) return undefined;
    const timeoutId = window.setTimeout(() => {
      setNotices(current => current.slice(1));
    }, NOTICE_DURATION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [notices]);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window) || Notification.permission !== 'default') return;
    await Notification.requestPermission();
  };

  if (!notices.length) return null;

  return (
    <div className="fixed right-4 top-4 z-[var(--z-toast,100)] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2" aria-live="polite" aria-label="Mensajes nuevos">
      {notices.map(notice => (
        <div key={notice.noticeId} className="rounded-xl border border-[rgba(var(--border),0.55)] bg-[rgb(var(--panel))] p-3 text-[rgb(var(--fg))] shadow-[0_14px_34px_rgba(var(--shadow),0.22)]">
          <button type="button" className="block w-full text-left" onClick={() => navigate('/chat')}>
            <span className="block text-sm font-semibold">Mensaje de {notice.senderName}</span>
            <span className="mt-0.5 block truncate text-xs text-[rgb(var(--muted))]">{notice.body}</span>
          </button>
          {typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default' ? (
            <button type="button" className="mt-2 text-xs font-medium text-[rgb(var(--brand))] underline" onClick={requestNotificationPermission}>
              Activar avisos del sistema
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}
